import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { saveLastSession } from '../../hooks/useLastSession';
import SEOComponent from '../common/SEOComponent';
import AdviceSection from '../layout/AdviceSection';
import InitialPrompt from './InitialPrompt';
import ErrorMessage from './ErrorMessage';
import VisualizationRouter from './VisualizationRouter';
import { detectLanguageFromPathname, getActionLabel } from '../../i18n';
import { getRouteBase } from '../../config/meidaisaiDemo';

/**
 * DashboardLayout - 目的ベース・グラフベース共通のレイアウトコンポーネント
 * 
 * @param {string} variant - 'purpose' または 'function'
 * @param {object} seoProps - SEO設定（title, description, keywords, url）
 */
function DashboardLayout({ variant, seoProps }) {
    const location = useLocation();
    const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
    const { 
        location: selectedLocation, 
        action: selectedAction,
        year: selectedYear,
        month: selectedMonth,
    } = useDashboard();
    
    const { data, calendarData, loading, error } = useDashboardData();
    const isMobile = useMediaQuery('(max-width:768px)');

    // カレンダー・ヒートマップ系は data が null で calendarData のみ入るため、両方を見る
    const hasAdviceData =
        Boolean(data) ||
        (Array.isArray(calendarData) && calendarData.length > 0);
    
    // アクションが場所を必要とするかチェック
    const actionRequiresLocation =
        selectedAction &&
        selectedAction !== 'foreigners_distribution' &&
        selectedAction !== 'foreigners_yearly_distribution';
    
    // 初期状態かどうか（アクションまたは場所が未選択）
    const isInitialState = !selectedAction || (actionRequiresLocation && !selectedLocation);

    const dashboardPath = `${getRouteBase(location.pathname)}${variant === 'function' ? '/function' : '/purpose'}`;
    const lang = isEnglish ? 'en' : 'ja';

    useEffect(() => {
        if (isInitialState || !selectedAction) return;
        const { label } = getActionLabel(selectedAction, lang);
        saveLastSession({
            action: selectedAction,
            location: selectedLocation || null,
            year: selectedYear,
            month: selectedMonth,
            label,
            dashboard_path: dashboardPath,
        });
    }, [
        isInitialState,
        selectedAction,
        selectedLocation,
        selectedYear,
        selectedMonth,
        dashboardPath,
        lang,
    ]);
    
    // テーマカラーを取得
    const themeColor = variant === 'purpose' 
        ? 'rgba(248, 249, 250, 0.3)'  // グレー（目的ベース）
        : 'rgba(250, 248, 255, 0.3)';  // パープル（グラフベース）
    
    const themeShadow = variant === 'purpose'
        ? 'inset 0 1px 3px rgba(74, 85, 104, 0.1)'
        : 'inset 0 1px 3px rgba(85, 60, 154, 0.1)';
    
    return (
        <>
            <SEOComponent {...seoProps} />
            
            {/* 初期状態のオーバーレイ */}
            {isInitialState && (
                <Box sx={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                }} />
            )}
            
            {/* メインコンテナ（1カラムレイアウト） */}
            <Box sx={{
                backgroundColor: themeColor,
                borderRadius: isMobile ? '0' : '8px',
                padding: isMobile ? '6px 4px 12px' : '12px 8px 16px',
                boxShadow: themeShadow,
                flex: '1 0 auto',
                position: 'relative'
            }}>
                {/* エラー表示 */}
                {error && <ErrorMessage error={error} />}
                
                {/* メインコンテンツエリア（全幅） */}
                <Box sx={{ 
                    maxWidth: 1200, 
                    mx: 'auto',
                    width: '100%'
                }}>
                    {isInitialState ? (
                        <InitialPrompt 
                            action={selectedAction} 
                            location={selectedLocation} 
                            variant={variant} 
                        />
                    ) : (
                        <>
                            <VisualizationRouter
                                action={selectedAction}
                                data={data}
                                loading={loading}
                                variant={variant}
                            />
                            
                            {/* 生成AIコーナー（旧・分析の手引きを統合） */}
                            {!loading && hasAdviceData && <AdviceSection variant={variant} />}
                            
                            {/* フィードバックボタン（AdviceSectionの下に配置） */}
                            {!loading && hasAdviceData && (
                                <Box sx={{ mt: 2, px: 1 }}>
                                    <Button
                                        variant="contained"
                                        color={variant === 'purpose' ? 'primary' : 'secondary'}
                                        fullWidth
                                        href="https://docs.google.com/forms/d/e/1FAIpQLSeEalJjup-hR6BN6M8MfETrPn3is0i-5Rskxz_rkEZvI7mvFw/viewform?usp=header"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            py: 1.5,
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                                                transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        {isEnglish ? 'Tap here to share your feedback' : 'ここをタップしてご意見をお聞かせください'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
}

DashboardLayout.propTypes = {
    variant: PropTypes.oneOf(['purpose', 'function']).isRequired,
    seoProps: PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        keywords: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
};

export default DashboardLayout;
